import React, {
	type ChangeEvent,
	useCallback,
	useState,
	useMemo,
	type PropsWithChildren,
	Fragment,
} from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import UrlEditViewEntryPoint from '@atlassian/jira-issue-field-single-line-text-editview-full/src/entrypoint.tsx';
import { UrlReadView } from '@atlassian/jira-issue-field-url-readview-full/src/ui/url/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { SingleLineTextEditViewProps } from '@atlassian/jira-issue-field-single-line-text-editview-full/src/ui/single-line-text/types.tsx';
import type {
	ClientEventValidator,
	ClientValidator,
} from '@atlassian/jira-issue-field-validation-handler/src/controllers/client-validation-handler/types.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import type { url_issueFieldUrlInlineEditFull_UrlInlineEditViewIsEditable_fragmentRef$key as UrlInlineEditViewIsEditableFragment } from './__generated__/url_issueFieldUrlInlineEditFull_UrlInlineEditViewIsEditable_fragmentRef.graphql';
import type { url_issueFieldUrlInlineEditFull_UrlInlineEditView_fragmentRef$key as UrlFragment } from './__generated__/url_issueFieldUrlInlineEditFull_UrlInlineEditView_fragmentRef.graphql';
import type {
	url_issueFieldUrl_UrlField_Mutation as UrlMutation,
	url_issueFieldUrl_UrlField_Mutation$data as UrlMutationData,
} from './__generated__/url_issueFieldUrl_UrlField_Mutation.graphql';
import messages from './messages.tsx';
import type { UrlInlineEditViewProps, UrlInlineEditViewIsEditableProps } from './types.tsx';
import { isAllowed } from './utils.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-url-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

export const UrlInlineEditViewIsEditable = ({
	attributes,
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupWidth,
	fragmentRef,
	spacing = 'compact',
	readViewFitContainerHeight,
	isEditable,
	isSimpleInlineSmartLinkEnabled,
	onSubmit,
	onSubmitSucceeded,
	onSubmitFailed,
	shouldRenderHoverPopover,
}: UrlInlineEditViewIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<UrlInlineEditViewIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment url_issueFieldUrlInlineEditFull_UrlInlineEditViewIsEditable_fragmentRef on JiraUrlField {
				id
				name
				fieldId
				type
				uri
				...url_issueFieldUrlReadviewFull_UrlReadView
			}
		`,
		fragmentRef,
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<UrlMutation>(graphql`
		mutation url_issueFieldUrl_UrlField_Mutation($input: JiraUpdateUrlFieldInput!)
		@raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateUrlField(input: $input) {
					success
					errors {
						message
					}
					field {
						...url_issueFieldUrlInlineEditFull_UrlInlineEditViewIsEditable_fragmentRef
					}
				}
			}
		}
	`);

	const { id: uniqueFieldId, name, fieldId, type: fieldType } = data;
	// #endregion Relay

	// #region State
	const { formatMessage } = useIntl();

	const uri = data.uri ?? '';
	const [updatedValue, setUpdatedValue] = useState<string>(uri);

	const { overriding } = useInlineEditFieldInjections();
	const { overrideLabel, overrideIsEditable } = overriding;

	const fieldName = useMemo(() => overrideLabel(name), [overrideLabel, name]);

	const isFieldEditable = useMemo(
		() => overrideIsEditable(isEditable),
		[overrideIsEditable, isEditable],
	);
	// #endregion State

	// #region Action
	const handleSubmit = useCallback(
		(value: string, { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(value);

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operation: {
							operation: 'SET',
							uri: value,
						},
					},
				},
				onCompleted: (response: UrlMutationData) => {
					if (response.jira?.updateUrlField?.success) {
						onSuccess();
					} else {
						onFail();
					}
				},
				onError: (error) => {
					onFail(error, error.message);
				},
				optimisticResponse: {
					jira: {
						updateUrlField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								fieldId,
								type: fieldType,
								name,
								uri: value,
							},
						},
					},
				},
			});
		},
		[commit, fieldId, fieldType, name, onSubmit, uniqueFieldId],
	);

	const validator: ClientEventValidator<string> = useMemo(() => {
		const change: ClientValidator<string> = (value) => {
			if (!isAllowed(value)) {
				return {
					type: 'error',
					message: formatMessage(messages.validUrl),
				};
			}

			return null;
		};

		return { change };
	}, [formatMessage]);

	const actions = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType,
		initialValue: uri,
		updatedValue,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		validator,
		clipboardComponentType: 'url',
	});

	const {
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
		handleCopy,
		handleCut,
		handlePaste,
		invalidMessage,
		isEditing,
	} = actions;

	const onChange = useCallback(
		({ target }: ChangeEvent<HTMLInputElement>) => {
			handleChange(target.value);
		},
		[handleChange],
	);
	// #endregion Action

	// #region View
	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<UrlReadView
					fragmentRef={data}
					isSimpleInlineSmartLinkEnabled={isSimpleInlineSmartLinkEnabled}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
				/>
			</MaybeUFOSegment>
		),
		[data, isSimpleInlineSmartLinkEnabled, shouldRenderHoverPopover],
	);

	const getEditViewProps = (fieldProps: ValidationFieldProps): SingleLineTextEditViewProps => ({
		...fieldProps,
		autoFocus: true,
		value: updatedValue,
		spacing,
		onChange,
		type: 'url',
		isInvalid: Boolean(invalidMessage),
		ariaLabel: fieldName,
	});

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupWidth={editViewPopupWidth}
			editViewPopupMinWidth="small"
			editViewEntryPoint={UrlEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
		/>
	);
};

export const UrlInlineEditView = ({
	fragmentRef,
	...props
}: UrlInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<UrlFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment url_issueFieldUrlInlineEditFull_UrlInlineEditView_fragmentRef on JiraUrlField {
				...url_issueFieldUrlInlineEditFull_UrlInlineEditViewIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);

	return (
		<UrlInlineEditViewIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
